import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, KeyRound, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import BrandAuthLayout from '../Brand/BrandAuthLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const check = async () => {
      if (!token) {
        setChecking(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/auth/reset-password/${token}`);
        setValid(res.data.valid);
      } catch (err) {
        setValid(false);
      } finally {
        setChecking(false);
      }
    };
    check();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('The two passwords do not match');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not set your new password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <BrandAuthLayout strapline="Password reset" testId="reset-password-page">
      <h1 className="flex items-center gap-2 text-lg text-cream">
        <KeyRound className="h-5 w-5 text-gold" /> Set a new password
      </h1>

      <div className="mt-5 space-y-4">
        {checking && (
          <p className="flex items-center gap-2 text-sm text-cream/60">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking your link…
          </p>
        )}

        {!checking && !valid && !done && (
          <div className="space-y-4" data-testid="reset-link-invalid">
            <p className="flex items-start gap-2 rounded-[10px] border border-red-400/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              This reset link is not valid any more. Links work once and expire after 1 hour.
            </p>
            <Button onClick={() => navigate('/login')} size="lg" className="w-full">
              Back to sign in
            </Button>
          </div>
        )}

        {!checking && valid && !done && (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label htmlFor="new-password" className="bd-label-dark">New password</label>
              <Input
                id="new-password" type="password" required minLength={8} placeholder="At least 8 characters"
                className="h-12 bd-input-dark"
                value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="reset-password-input"
              />
            </div>
            <div>
              <label htmlFor="repeat-password" className="bd-label-dark">Repeat new password</label>
              <Input
                id="repeat-password" type="password" required minLength={8} placeholder="Type it once more"
                className="h-12 bd-input-dark"
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-password-confirm"
              />
            </div>
            {error && (
              <p className="rounded-[10px] border border-red-400/40 bg-red-500/10 px-3 py-2 text-sm text-red-200" data-testid="reset-password-error">{error}</p>
            )}
            <Button type="submit" disabled={saving} size="lg" className="w-full" data-testid="reset-password-submit">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save new password
            </Button>
          </form>
        )}

        {done && (
          <div className="space-y-4" data-testid="reset-password-done">
            <p className="flex items-start gap-2 text-sm text-green-300">
              <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" /> Your password has been updated.
            </p>
            <Button onClick={() => navigate('/login')} size="lg" className="w-full">
              Sign in
            </Button>
          </div>
        )}
      </div>
    </BrandAuthLayout>
  );
}
